import { useQuery, useQueries, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { createBatchLoader } from "@/lib/batchLoader";
import { upsert } from "@/lib/cachePersistence";
import { entitiesService } from "@/services/entities.service";

export interface EntityDetailResult {
  entity: any;
  connectedEntities: any[];
}

// The unit write rule: upsert — a response updates the fields it carries
// and can never remove what a fuller response already wrote.
const upsertEntityUnit = (
  queryClient: QueryClient,
  entityId: string,
  fresh: EntityDetailResult | null,
): EntityDetailResult | null => {
  const prev = queryClient.getQueryData<EntityDetailResult>(["entity", entityId]);
  if (!fresh?.entity) return prev ?? null;
  return {
    entity: upsert(prev?.entity ?? null, fresh.entity),
    connectedEntities: fresh.connectedEntities?.length
      ? fresh.connectedEntities
      : prev?.connectedEntities ?? [],
  };
};

const makeEntityBatchLoader = (skipAuth: boolean) =>
  createBatchLoader<string, EntityDetailResult | null>(
    async (ids) => {
      const response = await entitiesService.getEntitiesBatch(ids, { allowUnlinked: true, skipAuth });
      const byId = new Map<string, EntityDetailResult | null>(ids.map(id => [id, null]));
      for (const entity of response.entities ?? []) {
        const id = (entity as { entity_id?: string })?.entity_id;
        if (id && byId.has(id)) byId.set(id, { entity, connectedEntities: [] });
      }
      return byId;
    },
    { retries: 1 },
  );

const entityCardLoader = makeEntityBatchLoader(false);
const entityCardLoaderAnon = makeEntityBatchLoader(true);

/**
 * Card-grade entity hydration: same ["entity", id] unit as useEntityDetail,
 * but any number of cards mounting in one tick costs a single /entities/batch
 * call. Cards read the shared unit; only the detail page fetches singly.
 */
export const useEntityCard = (entityId?: string, skipAuth = false) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["entity", entityId],
    enabled: Boolean(entityId),
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () =>
      upsertEntityUnit(
        queryClient,
        entityId!,
        await (skipAuth ? entityCardLoaderAnon : entityCardLoader).load(entityId!),
      ),
  });
};

export const useEntityDetail = (entityId?: string, initialData?: any, skipAuth = false) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["entity", entityId],
    enabled: Boolean(entityId),
    initialData,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EntityDetailResult | null> => {
      if (!entityId) throw new Error("Missing entity id");
      const entity = await entitiesService.getEntity(entityId, skipAuth);
      return upsertEntityUnit(queryClient, entityId, entity ? { entity, connectedEntities: [] } : null);
    },
  });
};

export interface EdgeInfo {
  edgeType: string;
  sourceEntityId: string;
  targetEntityId: string;
}

export interface ConnectedEntitiesResult {
  entities: any[];
  edgeTypes: Map<string, EdgeInfo>;
}

/**
 * Fetch connected entities for a given entity.
 * Returns entities and a Map of entity_id → edge_type.
 * Only use on detail pages — not for cards/lists.
 *
 * Always fetches the entity fresh from the API to ensure connected_entity_edges
 * is present — batch/preview fetches that populate the cache omit this field.
 */
export const useConnectedEntities = (entityId?: string, skipAuth = false) => {
  return useQuery({
    queryKey: ["entity", entityId, "connected"],
    enabled: Boolean(entityId),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ConnectedEntitiesResult> => {
      if (!entityId) return { entities: [], edgeTypes: new Map() };

      // Fetch entity directly — don't rely on cached data which may lack edge fields
      const entity = await entitiesService.getEntity(entityId, skipAuth);
      if (!entity) return { entities: [], edgeTypes: new Map() };

      // Prefer typed edges from the API, fall back to flat IDs
      const edges: Array<{ entity_id: string; edge_type: string; source_entity_id?: string; target_entity_id?: string }> =
        entity.connected_entity_edges ??
        (entity.connected_entity_ids ?? []).map((id: string) => ({
          entity_id: id,
          edge_type: 'unknown',
        }));

      const connectedIds = edges
        .map((e: any) => e.entity_id)
        .filter((id: string) => typeof id === 'string' && id);

      if (connectedIds.length === 0) return { entities: [], edgeTypes: new Map() };

      const edgeTypes = new Map<string, EdgeInfo>(
        edges.map((e) => [e.entity_id, {
          edgeType: e.edge_type,
          sourceEntityId: e.source_entity_id ?? entityId!,
          targetEntityId: e.target_entity_id ?? e.entity_id,
        }])
      );

      try {
        const batchResponse = await entitiesService.getEntitiesBatch(
          connectedIds,
          { allowUnlinked: true, skipAuth }
        );
        return { entities: batchResponse.entities || [], edgeTypes };
      } catch (err) {
        console.warn('[useConnectedEntities] Failed to fetch:', err);
        return { entities: [], edgeTypes: new Map() };
      }
    },
  });
};

/**
 * Fetch multiple entities in a single batch POST request.
 * Hydrates individual ['entity', id] caches inside queryFn so downstream
 * useEntityDetail calls get cache hits without any race condition.
 */
export const useEntitiesBatch = (entityIds: string[], skipAuth = false) => {
  const queryClient = useQueryClient();
  const stableKey = useMemo(() => [...entityIds].sort().join(','), [entityIds]);
  const ids = useMemo(() => entityIds.filter(Boolean), [stableKey]);

  const query = useQuery({
    queryKey: ["entities-batch", stableKey],
    queryFn: async () => {
      if (ids.length === 0) return { entities: [] as any[], count: 0 };
      const result = await entitiesService.getEntitiesBatch(ids, { allowUnlinked: true, skipAuth });
      // Hydrate individual entity caches inside queryFn — by the time React Query
      // transitions to "success" and triggers re-render, all ['entity', id] entries
      // are already populated. This prevents child useEntityDetail from refetching.
      for (const entity of result.entities ?? []) {
        const id = entity.entity_id || entity.id;
        if (!id) continue;
        queryClient.setQueryData(['entity', id], (prev?: EntityDetailResult) => ({
          entity: upsert(prev?.entity ?? null, entity),
          connectedEntities: prev?.connectedEntities ?? [],
        }));
      }
      return result;
    },
    enabled: ids.length > 0,
    staleTime: 5 * 60_000,
  });

  return query;
};

/**
 * Fetch multiple entities by ID using React Query's useQueries.
 * Each entity is cached individually, so subsequent single-entity requests benefit.
 */
export const useEntitiesDetail = (entityIds: string[], skipAuth = false) => {
  const queryClient = useQueryClient();
  // Stable key to avoid re-renders when array reference changes
  const stableIds = useMemo(() => [...entityIds].sort().join(','), [entityIds]);
  const ids = useMemo(() => entityIds.filter(Boolean), [stableIds]);

  const queries = useQueries({
    queries: ids.map(entityId => ({
      queryKey: ["entity", entityId],
      staleTime: 5 * 60 * 1000,
      retry: false,
      queryFn: async (): Promise<EntityDetailResult | null> =>
        upsertEntityUnit(
          queryClient,
          entityId,
          await (skipAuth ? entityCardLoaderAnon : entityCardLoader).load(entityId),
        ),
      enabled: Boolean(entityId),
    })),
  });

  const isLoading = queries.some(q => q.isLoading);
  const isError = queries.some(q => q.isError);

  const entitiesArray = useMemo(() => {
    return queries
      .map(q => q.data?.entity)
      .filter((e): e is any => e != null);
  }, [queries]);

  const entitiesMap = useMemo(() => {
    const map = new Map<string, any>();
    queries.forEach((q, i) => {
      if (q.data?.entity) {
        map.set(ids[i], q.data.entity);
      }
    });
    return map;
  }, [queries, ids]);

  return {
    queries,
    entitiesArray,
    entitiesMap,
    isLoading,
    isError,
    getEntity: (id: string) => entitiesMap.get(id),
  };
};
